import { useCallback, useMemo, useState } from 'react';
import {
  countExpensesForBranch,
  listExpensesForBranch,
} from '../infra/expensesRepo';
import type { ExpenseDueStatusFilter, ExpenseListQuery, ExpensePaymentStatusFilter, ExpenseSortBy } from '../domain/expenseTypes';
import { usePaginatedRemoteList } from '../../shared/data/usePaginatedRemoteList';
import { useDebouncedValue } from '../../shared/ui/hooks-ui/useDebouncedValue';
import { PRODUCT_SEARCH_DEBOUNCE_MS } from '../../shared/product-search/productSearchUx';
import { defaultExpensePeriodFrom, todayIsoDate, resolvePeriodDates } from '../infra/expenseListSql';
import {
  EXPENSES_LIST_PAGE_SIZE_STORAGE_KEY,
  EXPENSE_PERIOD_PRESETS,
  type ExpensePeriodPreset,
} from './expensesList.constants';
import { formatExpenseDate } from '../domain/expenseLabels';

type Params = {
  branchId: string | null | undefined;
  moduleEnabled: boolean;
};

export function useExpensesListFlow({ branchId, moduleEnabled }: Params) {
  const [periodPreset, setPeriodPreset] = useState<ExpensePeriodPreset>('30d');
  const [customFromDate, setCustomFromDate] = useState(defaultExpensePeriodFrom(30));
  const [customToDate, setCustomToDate] = useState(todayIsoDate());
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<ExpensePaymentStatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [supplierFilter, setSupplierFilter] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<ExpenseSortBy>('expense_date_desc');
  const [dueStatusFilter, setDueStatusFilter] = useState<ExpenseDueStatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const searchDebounced = useDebouncedValue(searchQuery.trim(), PRODUCT_SEARCH_DEBOUNCE_MS);

  const periodDates = useMemo(
    () => resolvePeriodDates(periodPreset, customFromDate, customToDate),
    [periodPreset, customFromDate, customToDate]
  );

  const listQuery = useMemo<ExpenseListQuery | null>(() => {
    if (!branchId) return null;
    return {
      branchId,
      fromDate: periodDates.fromDate,
      toDate: periodDates.toDate,
      paymentStatus: paymentStatusFilter,
      dueStatus: dueStatusFilter,
      categoryId: categoryFilter,
      supplierId: supplierFilter,
      search: searchDebounced || undefined,
      sortBy,
    };
  }, [branchId, periodDates, paymentStatusFilter, dueStatusFilter, categoryFilter, supplierFilter, searchDebounced, sortBy]);

  const criteriaKey = useMemo(
    () =>
      listQuery
        ? `${listQuery.branchId}|${listQuery.fromDate}|${listQuery.toDate}|${listQuery.paymentStatus}|${listQuery.dueStatus ?? ''}|${listQuery.categoryId ?? ''}|${listQuery.supplierId ?? ''}|${listQuery.search ?? ''}|${listQuery.sortBy}`
        : '',
    [listQuery]
  );

  const fetchPage = useCallback(
    ({ limit, offset, query }: { limit: number; offset: number; query: ExpenseListQuery }) =>
      listExpensesForBranch({ ...query, limit, offset }),
    []
  );

  const count = useCallback((query: ExpenseListQuery) => countExpensesForBranch(query), []);

  const paging = usePaginatedRemoteList({
    storageKey: EXPENSES_LIST_PAGE_SIZE_STORAGE_KEY,
    criteriaKey,
    query: listQuery ?? { branchId: '' },
    enabled: moduleEnabled && listQuery != null,
    logLabel: 'Expenses',
    fetchPage,
    count,
  });

  const hasActiveFilters =
    paymentStatusFilter !== 'all' ||
    dueStatusFilter !== 'all' ||
    categoryFilter != null ||
    supplierFilter != null ||
    searchDebounced.length > 0 ||
    periodPreset !== '30d' ||
    sortBy !== 'expense_date_desc';

  const activeFilterCount = [
    periodPreset !== '30d',
    paymentStatusFilter !== 'all',
    dueStatusFilter !== 'all',
    categoryFilter != null,
    supplierFilter != null,
    searchDebounced.length > 0,
    sortBy !== 'expense_date_desc',
  ].filter(Boolean).length;

  const clearFilters = useCallback(() => {
    setPeriodPreset('30d');
    setCustomFromDate(defaultExpensePeriodFrom(30));
    setCustomToDate(todayIsoDate());
    setPaymentStatusFilter('all');
    setDueStatusFilter('all');
    setCategoryFilter(null);
    setSupplierFilter(null);
    setSortBy('expense_date_desc');
    setSearchQuery('');
  }, []);

  const periodLabel = useMemo(() => {
    if (periodPreset === 'custom') {
      return `${formatExpenseDate(periodDates.fromDate)} — ${formatExpenseDate(periodDates.toDate)}`;
    }
    return EXPENSE_PERIOD_PRESETS.find((p) => p.id === periodPreset)?.label ?? '30 días';
  }, [periodPreset, periodDates]);

  return {
    expenses: paging.items,
    expensePageIndex: paging.pageIndex,
    expensePageSize: paging.pageSize,
    setExpensePageSize: paging.setPageSize,
    expenseHasMore: paging.hasMore,
    expenseLoading: paging.loading,
    expenseListError: paging.error,
    expenseTotalCount: paging.totalCount,
    loadExpenses: paging.load,
    reloadExpensesFromStart: paging.reloadFromStart,
    loadExpenseNextPage: paging.loadNextPage,
    loadExpensePreviousPage: paging.loadPreviousPage,
    expenseListRef: paging.flatListRef,
    expenseWideScrollRef: paging.scrollViewRef,
    listQuery,
    periodPreset,
    setPeriodPreset,
    customFromDate,
    setCustomFromDate,
    customToDate,
    setCustomToDate,
    periodLabel,
    paymentStatusFilter,
    setPaymentStatusFilter,
    categoryFilter,
    setCategoryFilter,
    supplierFilter,
    setSupplierFilter,
    sortBy,
    setSortBy,
    dueStatusFilter,
    setDueStatusFilter,
    searchQuery,
    setSearchQuery,
    hasActiveFilters,
    activeFilterCount,
    clearFilters,
  };
}
